import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import HighchartsMap from 'highcharts/highmaps';
import usaMap from '@highcharts/map-collection/countries/us/us-all.geo.json';
import { PageEvent } from '@angular/material/paginator';

import { WidgetContainerComponent } from '../my-brand-screen/widget-container/widget-container.component';
import { PieChartComponent } from '../../shared/components/charts/content-category-breakdown/pie-chart.component';
import { PerformanceOverTimeComponent } from '../../shared/components/charts/performance-over-time/performance-over-time.component';
import { OverviewCardComponent } from '../../shared/components/overview-tiles/overview-card/overview-card.component';
import { PaginatorComponent } from '../../shared/components/paginator/paginator.component';
import { SelectItem, SelectMode } from '../../shared/components/select-menu/select-menu.component';
import { BrandColors, ChartData } from '../../shared/types/common';
import { DeviceFilterType } from '../../services/types';
import { DatePickerComponent, FixedDateRanges } from '../../shared/components/date-picker/date-picker.component';
import { AssetVersionPipe } from '../../pipes/asset-version.pipe';
import { CardType, ValueType } from '../../shared/components/overview-tiles/types';
import { MyCompetitionPerformanceWidgetResponse } from '../my-competition-screen/types';
import { Format } from '../../services/util.service';
import { BrandsSelectionPanelComponent } from '../brands-selection-panel/brands-selection-panel.component';
import { SelectBrandItem } from '../../services/dashboard.service';
import { RibbonComponent } from '../my-campaigns-screen/ribbon/ribbon.component';
import { AuthorizationService } from '../../services/authorization.service';

import { SOCIAL_OVERVIEW_DATA } from './mock-assets/data/social-overview-data';
import { getLastNumDays } from '../../utils/time';

interface CreativeItem {
  id: string;
  type: string;
  brandName: string;
  brandColor: string;
  platform: string;
  imgUrl: string;
  cpm: number;
  impressions: string;
  description?: string;
}

interface ChannelBrand {
  brandName: string;
  segments: { label: string; percentage: number; color: string }[];
}

interface AdTypeGroup {
  label: string;
  brands: { brandName: string; percentage: number }[];
}

interface MapRegion {
  region: string;
  spend: number;
  impressions: number;
  reach: number;
  cpm: number;
}

const ALL_BRAND_NAMES = ['American Express', 'Chase', 'Visa', 'Mastercard', 'Discover'];
const COMPETITOR_COLORS = [BrandColors.BRAND_1, BrandColors.BRAND_2, BrandColors.BRAND_3, BrandColors.BRAND_4];

const BRAND_KEYS: Record<string, string> = {
  'American Express': 'american_express',
  'Chase': 'chase',
  'Visa': 'visa',
  'Mastercard': 'mastercard',
  'Discover': 'discover',
};

const BRAND_LOGOS: Record<string, string> = {
  'American Express': 'american-express-logo.png',
  'Chase': 'chase-logo.png',
  'Visa': 'visa-logo.jpg',
  'Mastercard': 'mastercard-logo.webp',
  'Discover': 'discover-logo.jpeg',
};

type MetricKey = 'spend' | 'impressions' | 'reach' | 'cpm';

@Component({
  selector: 'app-social-overview-screen',
  standalone: true,
  imports: [
    CommonModule,
    HighchartsChartModule,
    WidgetContainerComponent,
    PieChartComponent,
    PerformanceOverTimeComponent,
    OverviewCardComponent,
    PaginatorComponent,
    BrandsSelectionPanelComponent,
    DatePickerComponent,
    AssetVersionPipe,
    RibbonComponent
  ],
  templateUrl: './social-overview-screen.component.html',
  styleUrl: './social-overview-screen.component.scss'
})
export class SocialOverviewScreenComponent implements OnInit {
  private authorizationService = inject(AuthorizationService);

  protected readonly CardType = CardType;
  protected readonly ValueType = ValueType;
  protected readonly SelectMode = SelectMode;

  get showRibbonsAndBanner(): boolean {
    return this.authorizationService.isTrialUser || this.authorizationService.isExpiredUser;
  }

  overview = SOCIAL_OVERVIEW_DATA.overview;
  creativeItems: CreativeItem[] = SOCIAL_OVERVIEW_DATA.creativeGallery.items.map(i => ({ ...i }));
  sortedCreativeItems: CreativeItem[] = [];
  channelData: ChannelBrand[] = SOCIAL_OVERVIEW_DATA.channelDistribution as unknown as ChannelBrand[];
  adTypeData: AdTypeGroup[] = SOCIAL_OVERVIEW_DATA.adType as unknown as AdTypeGroup[];
  filteredAdTypeData: AdTypeGroup[] = [];
  mapData: MapRegion[] = SOCIAL_OVERVIEW_DATA.performanceMap as unknown as MapRegion[];
  brandNames: string[] = [];

  selectedBrandName = 'American Express';
  brandColorMap: Record<string, string> = {};
  myBrandItem: SelectBrandItem | undefined;
  myCompetitorsItems: SelectBrandItem[] = [];
  myBrandOptions: SelectBrandItem[] = [];

  // Second bar (filters - display only)
  protected readonly DeviceFilterType = DeviceFilterType;
  protected readonly deviceOptions: SelectItem<DeviceFilterType>[] = [
    { key: DeviceFilterType.ALL_DEVICES, label: DeviceFilterType.ALL_DEVICES, icon: 'svg/all-devices.svg' },
    { key: DeviceFilterType.DESKTOP, label: DeviceFilterType.DESKTOP, icon: 'svg/device-desktop.svg' },
    { key: DeviceFilterType.MOBILE, label: DeviceFilterType.MOBILE, icon: 'svg/device-mobile.svg' }
  ];
  protected countriesControl: FormControl<string[] | null> = new FormControl([]);
  protected deviceControl: FormControl<string | null> = new FormControl(DeviceFilterType.ALL_DEVICES);
  protected countriesOptions: SelectItem[] = [
    { key: 'US', label: 'United States' },
    { key: 'CA', label: 'Canada' },
    { key: 'GB', label: 'United Kingdom' },
    { key: 'DE', label: 'Germany' },
    { key: 'FR', label: 'France' },
  ];
  defaultDateRange = getLastNumDays(30);
  protected dateRangeGroup = new FormGroup({
    startDate: new FormControl<Date | null>(this.defaultDateRange.startDate || new Date(2025, 2, 1)),
    endDate: new FormControl<Date | null>(this.defaultDateRange.endDate || new Date(2025, 2, 31)),
    relativeDate: new FormControl<FixedDateRanges | undefined>(FixedDateRanges.LAST_30_DAYS) as FormControl<FixedDateRanges | undefined>,
  });

  overviewCards: { title: string; value: string; valueType: ValueType; description: string }[] = [];

  // Creative gallery
  readonly GALLERY_PAGE_SIZE = 8;
  galleryCurrentPage = 1;
  get pagedCreatives(): CreativeItem[] {
    const start = (this.galleryCurrentPage - 1) * this.GALLERY_PAGE_SIZE;
    return this.sortedCreativeItems.slice(start, start + this.GALLERY_PAGE_SIZE);
  }
  gallerySortOptions: SelectItem[] = [
    { key: 'impressions_desc', label: 'Impressions (Descending)' },
    { key: 'impressions_asc', label: 'Impressions (Ascending)' },
    { key: 'cpm_desc', label: 'CPM (Descending)' },
    { key: 'cpm_asc', label: 'CPM (Ascending)' },
  ];
  gallerySortControl = new FormControl<string | null>('impressions_desc');

  // Share of Voice
  sovChartData: ChartData[] = [];
  sovColors: string[] = [];
  sovPlatformOptions: SelectItem[] = [
    { key: 'all', label: 'All Platforms' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'instagram', label: 'Instagram' },
  ];
  sovPlatformControl = new FormControl<string | null>('all');

  // Performance Over Time
  performanceData: MyCompetitionPerformanceWidgetResponse | undefined;
  performanceFormat: Format = 'shortCurrencyNumber';
  brandColorsMap: Record<string, string> = {};
  performanceMetricOptions: SelectItem[] = [
    { key: 'spend', label: 'Spend' },
    { key: 'impressions', label: 'Impressions' },
    { key: 'reach', label: 'Reach' },
    { key: 'cpm', label: 'CPM' },
  ];
  performanceMetricControl = new FormControl<string | null>('spend');

  // Channel Distribution
  Highcharts: typeof Highcharts = Highcharts;
  channelChartOptions: Highcharts.Options = {};

  // Ad Type
  adTypePlatformOptions: SelectItem[] = [
    { key: 'all', label: 'All Platforms' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'instagram', label: 'Instagram' },
  ];
  adTypePlatformControl = new FormControl<string | null>('all');

  // Performance Map
  HighchartsMap: typeof HighchartsMap = HighchartsMap;
  mapChartOptions: Highcharts.Options = {};
  mapMetricOptions: SelectItem[] = [
    { key: 'spend', label: 'Spend' },
    { key: 'impressions', label: 'Impressions' },
    { key: 'reach', label: 'Reach' },
    { key: 'cpm', label: 'CPM' },
  ];
  mapMetricControl = new FormControl<string | null>('spend');

  @ViewChild('galleryPaginator') galleryPaginator!: PaginatorComponent;

  ngOnInit(): void {
    this.brandNames = SOCIAL_OVERVIEW_DATA.shareOfVoice.map(b => b.name);
    this.updateBrandAssignment();

    this.populateDescriptions();
    this.buildOverviewCards();
    this.buildSovData();
    this.sortGallery();
    this.buildPerformanceData();
    this.buildChannelChart();
    this.buildMapChart();
    this.onAdTypePlatformChange();
  }

  
  private populateDescriptions(): void {
    const templates: Record<string, string[]> = {
      'American Express': [
        'Earn rewards on every purchase with Amex.',
        'Unlock premium travel benefits today.',
        'Experience world-class dining perks.',
        'Discover exclusive member offers.',
        'Your card, your way — personalize rewards.',
        'Access airport lounges worldwide.',
        'Shop smarter with Amex cashback.',
        'Enjoy premium concierge services.',
        'Get more from every dollar spent.',
        'Amex — built for those who do more.',
        'See how Amex elevates your lifestyle.',
        'Card members enjoy priority access.',
        'Travel further with Amex points.',
        'Exclusive events. Exclusive access.',
        'Premium protection for every purchase.',
        'Smart spending starts here.',
      ],
      'Chase': [
        'Bank smarter with Chase rewards.',
        'Earn unlimited cashback on purchases.',
        'Chase Sapphire — travel your way.',
        'Your finances, simplified.',
        'Get more from every swipe.',
        'Freedom to earn — Freedom Unlimited.',
        'Explore premium travel perks.',
        'Banking made easy with Chase.',
        'Points that go further.',
        'Unlock your next adventure.',
        'Cashback that adds up fast.',
        'Chase — where your money works harder.',
        'Build credit, build confidence.',
        'Smart tools for everyday banking.',
        'Your rewards, your rules.',
        'Sapphire Reserve — redefine travel.',
      ],
      'Visa': [
        'Accepted everywhere you want to be.',
        'Pay securely, anywhere in the world.',
        'Visa — powering global commerce.',
        'Tap to pay, fast and easy.',
        'Trusted by millions worldwide.',
        'Seamless payments, every time.',
        'Your world, without boundaries.',
        'Travel with confidence using Visa.',
        'Contactless payments made simple.',
        'Visa — innovation in every transaction.',
        'Secure shopping online and in-store.',
        'Visa — connecting you to what matters.',
      ],
      'Mastercard': [
        'Start something priceless today.',
        'Mastercard — where convenience meets security.',
        'Tap into a world of possibilities.',
        'Priceless experiences await you.',
        'Payments reimagined with Mastercard.',
        'Fast, secure, and contactless.',
        'Unlock rewards with every purchase.',
        'Mastercard — trusted around the globe.',
        'Your everyday, elevated.',
        'Priceless surprises for cardholders.',
        'A smarter way to pay.',
        'Mastercard — innovation you can trust.',
        'Experiences that money can\'t buy.',
      ],
      'Discover': [
        'Cashback Match — only from Discover.',
        'No annual fee, ever.',
        'Discover it — rewards that really pay off.',
        'Get cash back on every purchase.',
        'Discover — banking without the fees.',
        'Earn 5% cashback in rotating categories.',
        'Your rewards, automatically matched.',
        'Freeze your card instantly if needed.',
        'Discover — where every dollar counts.',
        'Social security alerts, free for all.',
        'Simple rewards, no gimmicks.',
        'Discover — the card that pays you back.',
        'FICO score — free, every month.',
        'No foreign transaction fees.',
        'Cashback rewards that surprise you.',
        'Discover your potential.',
      ],
    };

    const counters: Record<string, number> = {};
    for (const item of this.creativeItems) {
      if (!item.description) {
        const pool = templates[item.brandName] || [`${item.type} by ${item.brandName}`];
        const idx = counters[item.brandName] || 0;
        item.description = pool[idx % pool.length];
        counters[item.brandName] = idx + 1;
      }
    }
  }

  // --- Platform helpers ---
  hasFacebook(platform: string): boolean {
    return platform.toLowerCase().includes('facebook');
  }

  hasInstagram(platform: string): boolean {
    return platform.toLowerCase().includes('instagram');
  }

  isVideo(item: CreativeItem): boolean {
    return item.type.toLowerCase().includes('video');
  }

  getCreativeImageUrl(item: CreativeItem): string {
    const filename = item.imgUrl.split('/').pop();
    return `/social-overview-assets/creative-images/Social Creatives/${filename}`;
  }

  onGalleryPageChange(event: PageEvent): void {
    this.galleryCurrentPage = event.pageIndex + 1;
  }

  onGallerySortChange(): void {
    this.sortGallery();
    this.galleryCurrentPage = 1;
    this.galleryPaginator?.goToPage(1);
  }

  private parseImpressionValue(str: string): number {
    const cleaned = str.replace(/,/g, '').trim();
    const match = cleaned.match(/^([\d.]+)\s*([KMB]?)$/i);
    if (!match) return 0;
    const num = parseFloat(match[1]);
    const suffix = match[2].toUpperCase();
    if (suffix === 'K') return num * 1_000;
    if (suffix === 'M') return num * 1_000_000;
    if (suffix === 'B') return num * 1_000_000_000;
    return num;
  }

  private sortGallery(): void {
    const key = this.gallerySortControl.value || 'impressions_desc';
    const copy = [...this.creativeItems];
    switch (key) {
      case 'impressions_desc':
        copy.sort((a, b) => this.parseImpressionValue(b.impressions) - this.parseImpressionValue(a.impressions));
        break;
      case 'impressions_asc':
        copy.sort((a, b) => this.parseImpressionValue(a.impressions) - this.parseImpressionValue(b.impressions));
        break;
      case 'cpm_desc':
        copy.sort((a, b) => b.cpm - a.cpm);
        break;
      case 'cpm_asc':
        copy.sort((a, b) => a.cpm - b.cpm);
        break;
    }
    this.sortedCreativeItems = copy;
  }

  // --- Overview ---
  private buildOverviewCards(): void {
    const ov = this.overview;
    this.overviewCards = [
      {
        title: 'My Share of Voice',
        value: ov.shareOfVoice,
        valueType: ValueType.NEUTRAL,
        description: '',
      },
      {
        title: 'Spend',
        value: ov.spend.replace(' vs competitors', ''),
        valueType: this.colorForMetric(ov.spend, true),
        description: ov.spend.includes('vs') ? 'vs competitors' : '',
      },
      {
        title: 'Reach',
        value: ov.reach.replace(' vs competitors', ''),
        valueType: this.colorForMetric(ov.reach, false),
        description: ov.reach.includes('vs') ? 'vs competitors' : '',
      },
      {
        title: 'CPM',
        value: ov.cpm.startsWith('~') ? '~' : ov.cpm.replace(' as competitors', '').replace(' Same as competitors', ''),
        valueType: ov.cpm.startsWith('~') ? ValueType.NEUTRAL : this.colorForMetric(ov.cpm, true),
        description: ov.cpm.startsWith('~') ? 'Same as competitors' : '',
      },
    ];
  }

  private colorForMetric(text: string, higherIsBad: boolean): ValueType {
    if (text.startsWith('~')) return ValueType.NEUTRAL;
    const isPositive = text.startsWith('+');
    const isNegative = text.startsWith('-');
    if (!isPositive && !isNegative) return ValueType.NEUTRAL;
    if (higherIsBad) {
      return isPositive ? ValueType.NEGATIVE : ValueType.POSITIVE;
    }
    return isPositive ? ValueType.POSITIVE : ValueType.NEGATIVE;
  }

  // --- Share of Voice ---
  private buildSovData(platform: string = 'all'): void {
    if (platform === 'all') {
      this.sovChartData = SOCIAL_OVERVIEW_DATA.shareOfVoice.map(item => ({
        name: item.name,
        key: BRAND_KEYS[item.name] || item.name,
        share: item.share,
      }));
      this.sovColors = SOCIAL_OVERVIEW_DATA.shareOfVoice.map(item => this.brandColorMap[item.name] || item.color);
      return;
    }

    const platformCreatives = this.creativeItems.filter(c =>
      c.platform.toLowerCase().includes(platform)
    );
    const brandImpressions: Record<string, number> = {};
    let total = 0;
    for (const c of platformCreatives) {
      const v = this.parseImpressionValue(c.impressions);
      brandImpressions[c.brandName] = (brandImpressions[c.brandName] || 0) + v;
      total += v;
    }
    const sovBrands = SOCIAL_OVERVIEW_DATA.shareOfVoice;
    this.sovChartData = sovBrands.map(b => ({
      name: b.name,
      key: BRAND_KEYS[b.name] || b.name,
      share: total > 0 ? Math.round(((brandImpressions[b.name] || 0) / total) * 10000) / 100 : 0,
    }));
    this.sovColors = sovBrands.map(b => this.brandColorMap[b.name] || b.color);
  }

  onSovPlatformChange(): void {
    this.buildSovData(this.sovPlatformControl.value || 'all');
  }

  // --- Performance Over Time ---
  private formatDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  }

  buildPerformanceData(): void {
    const metric = (this.performanceMetricControl.value || 'spend') as MetricKey;
    const raw = SOCIAL_OVERVIEW_DATA.performanceOverTime;

    const firstBrand = raw[0];
    const rest = raw.slice(1);
    this.performanceData = {
      myBrand: {
        brandKey: BRAND_KEYS[firstBrand.name] || firstBrand.name,
        brandName: firstBrand.name,
        dailyPerformance: firstBrand.data.map((d: any) => ({
          date: this.formatDate(d.date),
          value: this.roundForDisplay(d[metric] ?? 0),
        })),
      },
      myCompetitors: rest.map((brand) => ({
        brandKey: BRAND_KEYS[brand.name] || brand.name,
        brandName: brand.name,
        dailyPerformance: brand.data.map((d: any) => ({
          date: this.formatDate(d.date),
          value: this.roundForDisplay(d[metric] ?? 0),
        })),
      })),
      isUnderDateThreshold: false,
    };
  }

  private roundForDisplay(value: number): number {
    if (value >= 1_000_000) return Math.round(value / 100_000) * 100_000;
    if (value >= 1_000) return Math.round(value / 100) * 100;
    return Math.round(value * 100) / 100;
  }

  onPerformanceMetricChange(): void {
    const metric = this.performanceMetricControl.value;
    switch (metric) {
      case 'spend':
        this.performanceFormat = 'shortCurrencyNumber';
        break;
      case 'impressions':
        this.performanceFormat = 'shortNumber';
        break;
      case 'reach':
        this.performanceFormat = 'shortNumber';
        break;
      case 'cpm':
        this.performanceFormat = 'currency';
        break;
      default:
        this.performanceFormat = 'shortCurrencyNumber';
    }
    this.buildPerformanceData();
  }

  // --- Channel Distribution ---
  private buildChannelChart(): void {
    const categories = this.channelData.map(b => b.brandName);
    const channelLabels = this.channelData[0]?.segments.map(s => s.label) || [];

    const series: Highcharts.SeriesColumnOptions[] = channelLabels.map((label, i) => ({
      type: 'column' as const,
      name: label,
      color: this.channelData[0]?.segments[i]?.color,
      data: this.channelData.map(brand => brand.segments[i]?.percentage ?? 0),
      borderRadius: 0,
    }));

    this.channelChartOptions = {
      chart: { type: 'column', height: 350 },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: {
        categories,
        lineColor: '#DEE0E4',
        tickLength: 0,
        labels: {
          useHTML: true,
          formatter: function (this: any) {
            const name = this.value as string;
            const logo = BRAND_LOGOS[name];
            const logoUrl = logo ? `/social-overview-assets/brand-logos/${logo}` : '';
            return logoUrl
              ? `<div style="text-align:center"><img src="${logoUrl}" style="width:24px;height:24px;border-radius:4px;object-fit:contain;display:block;margin:0 auto 4px" /><span style="font-family:Poppins;font-size:12px;color:#34363A;font-weight:500">${name}</span></div>`
              : `<span style="font-family:Poppins;font-size:12px;color:#34363A;font-weight:500">${name}</span>`;
          },
        },
      },
      yAxis: {
        min: 0, max: 100,
        title: { text: '' },
        gridLineColor: '#EBEDEF',
        labels: { format: '{value}%', style: { fontFamily: 'Poppins', fontSize: '11px', color: '#686D75' } },
        tickInterval: 20,
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0,
          pointWidth: 72,
          dataLabels: {
            enabled: true, format: '{y}%',
            style: { fontFamily: 'Poppins', fontSize: '11px', fontWeight: '500', color: '#FFFFFF', textOutline: 'none' },
          },
        },
      },
      legend: {
        align: 'left', symbolRadius: 5,
        itemStyle: { fontFamily: 'Poppins', fontSize: '12px', fontWeight: '400', color: '#686D75' },
      },
      tooltip: {
        useHTML: true, shadow: false, backgroundColor: '#FFFFFF', borderColor: '#DEE0E4',
        style: { fontFamily: 'Poppins', fontSize: '12px' },
        pointFormat: '<b>{series.name}</b>: {point.y}%',
      },
      series,
    };
  }

  // --- Ad Type ---
  onAdTypePlatformChange(): void {
    const platform = this.adTypePlatformControl.value || 'all';

    const creatives = platform === 'all'
      ? this.creativeItems
      : this.creativeItems.filter(c => c.platform.toLowerCase().includes(platform));

    const brandCounts: Record<string, { image: number; video: number; total: number }> = {};
    for (const brand of this.brandNames) {
      brandCounts[brand] = { image: 0, video: 0, total: 0 };
    }
    for (const c of creatives) {
      if (!brandCounts[c.brandName]) continue;
      brandCounts[c.brandName].total++;
      if (c.type.toLowerCase().includes('video')) {
        brandCounts[c.brandName].video++;
      } else {
        brandCounts[c.brandName].image++;
      }
    }

    this.filteredAdTypeData = [
      {
        label: 'Image',
        brands: this.brandNames.map(name => ({
          brandName: name,
          percentage: brandCounts[name].total > 0
            ? Math.round((brandCounts[name].image / brandCounts[name].total) * 1000) / 10
            : 0,
        })),
      },
      {
        label: 'Video',
        brands: this.brandNames.map(name => ({
          brandName: name,
          percentage: brandCounts[name].total > 0
            ? Math.round((brandCounts[name].video / brandCounts[name].total) * 1000) / 10
            : 0,
        })),
      },
    ];
  }

  // --- Performance Map ---
  buildMapChart(): void {
    const metric = (this.mapMetricControl.value || 'spend') as MetricKey;
    const metricLabel = this.mapMetricOptions.find(o => o.key === metric)?.label || 'Spend';

    const values = this.mapData.map(item => item[metric]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    const brandIdx = ALL_BRAND_NAMES.indexOf(this.selectedBrandName);
    const percentData: [string, number][] = this.mapData.map((item, i) => {
      const base = (values[i] - min) / range * 200 - 100;
      const shift = Math.sin((i + 1) * (brandIdx + 1) * 2.654) * 40;
      const clamped = Math.max(-100, Math.min(100, base + shift));
      return [
        `us-${item.region.toLowerCase()}`,
        Math.round(clamped * 10) / 10,
      ];
    });

    this.mapChartOptions = {
      chart: { map: usaMap, height: 440, animation: false },
      title: { text: undefined },
      credits: { enabled: false },
      mapNavigation: { enabled: false },
      legend: { enabled: false },
      colorAxis: {
        min: -100, max: 100,
        stops: [
          [0, '#7B2D8E'],
          [0.15, '#5246B5'],
          [0.3, '#3068A8'],
          [0.45, '#28989E'],
          [0.55, '#50C8A0'],
          [0.7, '#8DD680'],
          [0.85, '#C8DC50'],
          [1, '#F0C020'],
        ],
        labels: {
          style: { fontFamily: 'Poppins', fontSize: '11px' },
          formatter: function (this: any) {
            return `${this.value}%`;
          },
        },
      },
      plotOptions: {
        map: {
          states: { hover: { enabled: false } },
        },
      },
      series: [{
        type: 'map' as any,
        name: metricLabel,
        data: percentData,
        nullColor: '#BDC2CA',
        borderColor: '#FFFFFF',
        borderWidth: 1,
        allowPointSelect: false,
        dataLabels: { enabled: false },
      }],
      tooltip: {
        useHTML: true, shadow: false, backgroundColor: 'transparent',
        formatter: function (this: any) {
          const name = this.point?.name;
          const value = this.point?.value;
          const sign = value >= 0 ? '+' : '';
          return `<div style="padding:12px;background:#fff;border-radius:4px;box-shadow:0 0 8px 2px rgba(0,0,0,0.15);font-family:Poppins;font-size:12px;color:#34363A;">
            <div style="font-weight:700;">${name}</div>
            <div>${metricLabel}: ${sign}${value}% vs competitors</div>
          </div>`;
        },
      },
    };
  }

  onMapMetricChange(): void {
    this.buildMapChart();
  }

  switchMapTab(tab: string): void {
    // Mock page only has US data
  }

  getBrandColor(brandName: string): string {
    return this.brandColorMap[brandName] || '#888';
  }

  getBrandLogoUrl(brandName: string): string {
    const filename = BRAND_LOGOS[brandName];
    return filename ? `/social-overview-assets/brand-logos/${filename}` : '';
  }

  onMyBrandChanged(brandKey: string): void {
    const brandName = Object.entries(BRAND_KEYS).find(([_, key]) => key === brandKey)?.[0];
    if (brandName && brandName !== this.selectedBrandName) {
      this.selectedBrandName = brandName;
      this.updateBrandAssignment();
      this.buildSovData(this.sovPlatformControl.value || 'all');
      this.buildPerformanceData();
      this.buildChannelChart();
      this.buildMapChart();
      this.onAdTypePlatformChange();
    }
  }

  private updateBrandAssignment(): void {
    this.brandColorMap = {};
    this.brandColorMap[this.selectedBrandName] = BrandColors.MY_BRAND;

    const competitors = ALL_BRAND_NAMES.filter(b => b !== this.selectedBrandName);
    competitors.forEach((name, i) => {
      this.brandColorMap[name] = COMPETITOR_COLORS[i];
    });

    this.myBrandItem = this.toBrandItem(this.selectedBrandName, BrandColors.MY_BRAND);
    this.myCompetitorsItems = competitors.map((name, i) =>
      this.toBrandItem(name, COMPETITOR_COLORS[i])
    );
    this.myBrandOptions = ALL_BRAND_NAMES.map(name =>
      this.toBrandItem(name, this.brandColorMap[name])
    );

    this.brandColorsMap = {};
    for (const [name, key] of Object.entries(BRAND_KEYS)) {
      this.brandColorsMap[key] = this.brandColorMap[name];
    }

    this.brandNames = [this.selectedBrandName, ...competitors];
  }

  private toBrandItem(name: string, color: string): SelectBrandItem {
    return {
      label: name,
      key: BRAND_KEYS[name],
      color,
      icon: `/social-overview-assets/brand-logos/${BRAND_LOGOS[name]}`,
    };
  }

  onConnectClick(): void {
    const { firstName, lastName, email, companyName } = this.authorizationService.userProfile;
    const fullName = `${firstName} ${lastName}`.trim();

    const baseUrl = 'https://calendly.com/d/cwrm-hv7-h2j/browsi-insights-demo-call';
    const params = new URLSearchParams();

    if (fullName) {
      params.set('name', fullName);
    }
    if (email) {
      params.set('email', email);
    }
    if (companyName) {
      params.set('a1', companyName);
    }

    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
    window.open(url, '_blank');
  }
}
